import { useEffect, useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

/**
 * Engineered Clarity — GSAP Reveal Animations
 *
 * Unified hook for scroll-triggered reveal animations
 * that match the brand's refined, understated motion language.
 *
 * Design principles:
 * - Subtle, never flashy
 * - Eased entrances (cubic-bezier feel)
 * - Staggered children for rhythm
 * - Copper accent elements get a slight delay
 */

type RevealVariant = 'fade-up' | 'fade-in' | 'slide-left' | 'slide-right' | 'scale' | 'stagger-children';

interface UseGSAPRevealOptions {
  variant?: RevealVariant;
  duration?: number;
  delay?: number;
  start?: string;
  staggerAmount?: number;
  childSelector?: string;
  distance?: number;
}

export const useGSAPReveal = (options: UseGSAPRevealOptions = {}) => {
  const ref = useRef<HTMLDivElement>(null);

  const {
    variant = 'fade-up',
    duration = 0.8,
    delay = 0,
    start = 'top 85%',
    staggerAmount = 0.1,
    childSelector = ':scope > *',
    distance = 30,
  } = options;

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    let ctx = gsap.context(() => {
      const ease = 'power3.out';

      switch (variant) {
        case 'fade-up':
          gsap.from(el, {
            opacity: 0,
            y: distance,
            duration,
            delay,
            ease,
            scrollTrigger: {
              trigger: el,
              start,
              once: true,
            },
          });
          break;

        case 'fade-in':
          gsap.from(el, {
            opacity: 0,
            duration,
            delay,
            ease,
            scrollTrigger: {
              trigger: el,
              start,
              once: true,
            },
          });
          break;

        case 'slide-left':
          gsap.from(el, {
            opacity: 0,
            x: distance,
            duration,
            delay,
            ease,
            scrollTrigger: {
              trigger: el,
              start,
              once: true,
            },
          });
          break;

        case 'slide-right':
          gsap.from(el, {
            opacity: 0,
            x: -distance,
            duration,
            delay,
            ease,
            scrollTrigger: {
              trigger: el,
              start,
              once: true,
            },
          });
          break;

        case 'scale':
          gsap.from(el, {
            opacity: 0,
            scale: 0.95,
            duration,
            delay,
            ease,
            scrollTrigger: {
              trigger: el,
              start,
              once: true,
            },
          });
          break;

        case 'stagger-children':
          const children = el.querySelectorAll(childSelector);
          if (children.length > 0) {
            gsap.from(children, {
              opacity: 0,
              y: distance * 0.6,
              duration,
              delay,
              ease,
              stagger: staggerAmount,
              scrollTrigger: {
                trigger: el,
                start,
                once: true,
              },
            });
          }
          break;
      }
    }, el);

    return () => ctx.revert();
  }, [variant, duration, delay, start, staggerAmount, childSelector, distance]);

  return ref;
};

/**
 * Hook for the copper accent line animation
 * The line grows from 0 to full width when scrolled into view
 */
export const useCopperLineReveal = (width: number = 40) => {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    let ctx = gsap.context(() => {
      gsap.from(el, {
        scaleX: 0,
        transformOrigin: 'left center',
        duration: 0.6,
        delay: 0.1,
        ease: 'power2.out',
        scrollTrigger: {
          trigger: el,
          start: 'top 90%',
          once: true,
        },
      });
    }, el);

    return () => ctx.revert();
  }, [width]);

  return ref;
};

/**
 * Parallax scroll effect for background elements
 * Subtle vertical movement for depth
 */
export const useParallax = (speed: number = 0.1) => {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    let ctx = gsap.context(() => {
      gsap.to(el, {
        y: () => speed * 100,
        ease: 'none',
        scrollTrigger: {
          trigger: el,
          start: 'top bottom',
          end: 'bottom top',
          scrub: true,
        },
      });
    }, el);

    return () => ctx.revert();
  }, [speed]);

  return ref;
};

export default useGSAPReveal;
